import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { RefreshCw, CloudUpload, Wifi, WifiOff, AlertTriangle } from "lucide-react";
import { counts, subscribe, syncAll, retryFailed, startAutoSync } from "@/lib/offline";
import { useT } from "@/lib/i18n";
import { toast } from "sonner";

/**
 * Fixed banner for Helpers that shows offline / sync state.
 * Zero visual impact when there is nothing pending and the device is online.
 */
export default function SyncBanner({ visible = true }) {
  const { t } = useT();
  const [c, setC] = useState({ total: 0, pending: 0, syncing: 0, synced: 0, failed: 0 });
  const [online, setOnline] = useState(navigator.onLine);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const refresh = () => counts().then(setC);
    refresh();
    const unsub = subscribe(refresh);
    const dispose = startAutoSync();
    const upd = () => setOnline(navigator.onLine);
    window.addEventListener("online", upd);
    window.addEventListener("offline", upd);
    return () => { unsub(); dispose(); window.removeEventListener("online", upd); window.removeEventListener("offline", upd); };
  }, []);

  const doSync = async () => {
    if (!online) { toast.error("You are offline — sync will run automatically when connected"); return; }
    setBusy(true);
    try {
      const r = await syncAll({ force: true });
      if (r.synced) toast.success(`Synced ${r.synced} record(s)`);
    } finally { setBusy(false); }
  };
  const doRetry = async () => {
    setBusy(true);
    try { await retryFailed(); } finally { setBusy(false); }
  };

  if (!visible) return null;
  const nothing = online && c.pending === 0 && c.failed === 0 && c.syncing === 0 && c.dead === 0;
  if (nothing) {
    return (
      <div data-testid="sync-banner" role="status" aria-live="polite" className="flex justify-end mb-3 no-print">
        <span className="am-chip am-chip-ok text-[10px]" title="Online — everything is synced" data-testid="online-mode-chip">
          <Wifi className="w-3 h-3" /> {t("offline.onlineMode")}
        </span>
      </div>
    );
  }
  return (
    <div data-testid="sync-banner" role="status" aria-live="polite" className="am-card p-3 mb-4 flex flex-wrap items-center gap-3 no-print">
      <span className={`am-chip ${online ? "am-chip-ok" : "bg-red-100 text-red-800"}`} data-testid={online ? "online-mode-chip" : "offline-mode-chip"}>
        {online ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />} {online ? t("offline.onlineMode") : t("offline.offlineMode")}
      </span>
      <div className="text-sm">
        <span className="font-semibold">{c.pending}</span> <span className="text-muted-foreground">pending</span>
        {c.syncing > 0 && <> · <span className="font-semibold">{c.syncing}</span> <span className="text-muted-foreground">syncing…</span></>}
        {c.synced > 0 && <> · <span className="font-semibold text-emerald-700">{c.synced}</span> <span className="text-muted-foreground">synced</span></>}
        {c.failed > 0 && <> · <span className="font-semibold text-red-700 inline-flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" />{c.failed} failed</span></>}
        {c.dead > 0 && <> · <span className="font-semibold text-red-900">{c.dead} dead</span></>}
      </div>
      <div className="ml-auto flex gap-2">
        {c.failed > 0 && (
          <Button size="sm" variant="outline" className="rounded-full" onClick={doRetry} disabled={busy} data-testid="sync-retry-btn">
            <RefreshCw className={`w-4 h-4 mr-1 ${busy ? "animate-spin" : ""}`} /> Retry failed
          </Button>
        )}
        <Button size="sm" className="am-btn-primary rounded-full" onClick={doSync} disabled={busy || !online} data-testid="sync-now-btn">
          <CloudUpload className={`w-4 h-4 mr-1 ${busy ? "animate-pulse" : ""}`} /> Sync now
        </Button>
      </div>
    </div>
  );
}
